import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

const WAS_LOGGED_IN_KEY = "reagent.wasLoggedIn";

function setWasLoggedIn(v) {
  try {
    if (v) window.localStorage.setItem(WAS_LOGGED_IN_KEY, "1");
    else window.localStorage.removeItem(WAS_LOGGED_IN_KEY);
  } catch (e) { /* localStorage may be unavailable */ }
}

function getWasLoggedIn() {
  try { return window.localStorage.getItem(WAS_LOGGED_IN_KEY) === "1"; }
  catch (e) { return false; }
}

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setWasLoggedIn(true);
      return data;
    } catch (e) {
      const status = e?.response?.status;
      // If we previously had a session and /auth/me now 401s, surface the
      // "session expired" affordance on the login page. Cold visitors with no
      // prior session just see the normal /login screen.
      if (status === 401 && getWasLoggedIn() && typeof window !== "undefined") {
        setWasLoggedIn(false);
        const path = window.location.pathname;
        if (path !== "/login" && !window.location.search.includes("expired=1")) {
          window.location.replace("/login?expired=1");
          return null;
        }
      }
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    setWasLoggedIn(true);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    setWasLoggedIn(true);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
    setWasLoggedIn(false);
  };

  const updateMe = async (payload) => {
    const { data } = await api.patch("/auth/me", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh, updateMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
